import { useState } from 'react';
import {
  Chip, IconButton, Tooltip, Stack, TextField, MenuItem, Menu, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Divider, Box, Alert, Snackbar,
} from '@mui/material';
import { Visibility, MoreVert, Add } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../../components/PageHeader';
import { DataTable, type Column } from '../../../components/DataTable';
import { technicianOnboardingApi, OPERATIONAL_STATUSES, type TechnicianApplication, type TechnicianReviewDecision } from '../../../api/technicianOnboarding.api';
import { extractApiError } from '../../../api/error';
import { useAuth } from '../../../hooks/useAuth';

const human = (s?: string | null) => (s ? s.replace(/_/g, ' ') : '—');
const DECISION_LABEL: Record<TechnicianReviewDecision, string> = { verify: 'Verify', approve: 'Approve', reject: 'Reject', send_back: 'Send Back', request_clarification: 'Request Clarification' };
const DECISION_COLOR: Record<TechnicianReviewDecision, 'success' | 'error' | 'warning' | 'info'> = { verify: 'success', approve: 'success', reject: 'error', send_back: 'warning', request_clarification: 'info' };
const STAGE_DECISIONS: Record<string, TechnicianReviewDecision[]> = {
  verification: ['verify', 'reject', 'send_back', 'request_clarification'],
  approval: ['approve', 'reject', 'send_back', 'request_clarification'],
};
const statusColor = (s: string): 'default' | 'success' | 'error' | 'warning' | 'info' =>
  ['approved', 'active', 'verified'].includes(s) ? 'success' : s === 'rejected' ? 'error' : ['sent_back', 'clarification_required'].includes(s) ? 'warning' : s === 'draft' ? 'default' : 'info';
const opColor = (s?: string | null): 'default' | 'success' | 'error' | 'warning' =>
  s === 'active' || s === 'deployed' ? 'success' : s === 'blacklisted' ? 'error' : s === 'inactive' || s === 'resigned' ? 'default' : 'warning';
const PENDING = ['submitted', 'verified', 'clarification_required'];

export function TechnicianApplicationsPage() {
  const { hasPermission } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [status, setStatus] = useState('');
  const [reviewId, setReviewId] = useState<number | null>(null);
  const [statusFor, setStatusFor] = useState<TechnicianApplication | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const canCreate = hasPermission('technician_onboarding.create');
  const canReview = hasPermission('technician_onboarding.review');
  const canSubmit = hasPermission('technician_onboarding.submit');
  const canStatus = hasPermission('technician_onboarding.change_status');

  const { data, isLoading } = useQuery({ queryKey: ['technician-applications', page, limit, status], queryFn: () => technicianOnboardingApi.list({ page, limit, status: status || undefined }) });
  const refresh = () => qc.invalidateQueries({ queryKey: ['technician-applications'] });
  const run = (p: Promise<unknown>, msg: string) => p.then(() => { setToast(msg); refresh(); }).catch((e) => setToast(extractApiError(e)));
  const newApp = () => technicianOnboardingApi.create({ technician_name: 'New Technician' }).then((a) => { refresh(); navigate(`/technician-applications/${a.id}/edit`); }).catch((e) => setToast(extractApiError(e)));

  const columns: Column<TechnicianApplication>[] = [
    { key: 'application_code', label: 'Technician ID', width: 150 },
    { key: 'technician_name', label: 'Name' },
    { key: 'technician_type', label: 'Type', render: (r) => human(r.technician_type) },
    { key: 'mobile', label: 'Mobile', render: (r) => r.mobile ?? '—' },
    { key: 'experience_level', label: 'Level', render: (r) => human(r.experience_level) },
    { key: 'status', label: 'Status', render: (r) => <Chip size="small" color={statusColor(r.status)} label={human(r.status)} /> },
    { key: 'operational_status', label: 'Operational', render: (r) => (r.technician_id ? <Chip size="small" color={opColor(r.operational_status)} label={human(r.operational_status)} /> : '—') },
    { key: 'login_user_id', label: 'Login', render: (r) => (r.login_user_id ? <Chip size="small" color="info" label="Yes" /> : '—') },
  ];

  return (
    <>
      <PageHeader title="Technician Applications" subtitle="Technician lifecycle — Draft → Submitted → Verified → Approved → Active → Deployed."
        action={canCreate && <Button startIcon={<Add />} variant="contained" onClick={newApp}>New Technician</Button>} />

      <Stack direction="row" mb={2}>
        <TextField select size="small" label="Status" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} sx={{ minWidth: 240 }}>
          <MenuItem value="">All</MenuItem>
          {['draft', 'submitted', 'verified', 'approved', 'active', 'sent_back', 'clarification_required', 'rejected'].map((s) => <MenuItem key={s} value={s}>{human(s)}</MenuItem>)}
        </TextField>
      </Stack>

      <DataTable columns={columns} rows={data?.data ?? []} loading={isLoading} page={page} limit={limit} total={data?.meta.total ?? 0}
        onPageChange={setPage} onLimitChange={(l) => { setLimit(l); setPage(1); }} rowKey={(r) => r.id}
        actions={(r) => (
          <RowActions row={r} perms={{ canReview, canSubmit, canStatus, canCreate }}
            onView={() => setReviewId(r.id)}
            onEdit={() => navigate(`/technician-applications/${r.id}/edit`)}
            onSubmit={() => run(technicianOnboardingApi.submit(r.id), 'Submitted for review')}
            onWithdraw={() => run(technicianOnboardingApi.withdraw(r.id), 'Application withdrawn')}
            onDelete={() => run(technicianOnboardingApi.remove(r.id), 'Draft deleted')}
            onReopen={() => run(technicianOnboardingApi.reopen(r.id), 'Application reopened')}
            onClone={() => technicianOnboardingApi.clone(r.id).then((a) => { setToast('Cloned to a new draft'); refresh(); navigate(`/technician-applications/${a.id}/edit`); }).catch((e) => setToast(extractApiError(e)))}
            onChangeStatus={() => setStatusFor(r)}
          />
        )} />

      {reviewId && <ReviewDialog id={reviewId} canReview={canReview} onClose={() => setReviewId(null)} />}
      {statusFor && <StatusDialog app={statusFor} onClose={() => setStatusFor(null)} onDone={() => { setStatusFor(null); setToast('Status updated'); refresh(); }} />}
      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)}><Alert severity="info" onClose={() => setToast(null)}>{toast}</Alert></Snackbar>
    </>
  );
}

interface Perms { canReview: boolean; canSubmit: boolean; canStatus: boolean; canCreate: boolean }

function RowActions({ row, perms, onView, onEdit, onSubmit, onWithdraw, onDelete, onReopen, onClone, onChangeStatus }: {
  row: TechnicianApplication; perms: Perms;
  onView: () => void; onEdit: () => void; onSubmit: () => void; onWithdraw: () => void; onDelete: () => void; onReopen: () => void; onClone: () => void; onChangeStatus: () => void;
}) {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const close = () => setAnchor(null);
  const s = row.status;
  const isDraft = s === 'draft' || s === 'sent_back' || s === 'clarification_required';
  const isPending = PENDING.includes(s);
  const items: Array<{ label: string; onClick: () => void; color?: string }> = [];
  if (isDraft && perms.canSubmit) { items.push({ label: 'Edit', onClick: onEdit }); if (s !== 'clarification_required') items.push({ label: 'Submit', onClick: onSubmit }); items.push({ label: 'Delete', onClick: onDelete, color: 'error' }); }
  if (isPending) { if (perms.canReview) items.push({ label: 'Review', onClick: onView }); if (perms.canSubmit) items.push({ label: 'Withdraw', onClick: onWithdraw, color: 'warning' }); }
  if (s === 'active' && perms.canStatus) items.push({ label: 'Change Status', onClick: onChangeStatus });
  if (s === 'rejected') { if (perms.canReview) items.push({ label: 'Reopen', onClick: onReopen }); if (perms.canCreate) items.push({ label: 'Clone', onClick: onClone }); }

  return (
    <>
      <Tooltip title="View"><IconButton size="small" onClick={onView}><Visibility fontSize="small" /></IconButton></Tooltip>
      {items.length > 0 && (
        <>
          <IconButton size="small" onClick={(e) => setAnchor(e.currentTarget)}><MoreVert fontSize="small" /></IconButton>
          <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={close}>
            {items.map((it) => <MenuItem key={it.label} onClick={() => { close(); it.onClick(); }} sx={it.color ? { color: `${it.color}.main` } : undefined}>{it.label}</MenuItem>)}
          </Menu>
        </>
      )}
    </>
  );
}

function ReviewDialog({ id, canReview, onClose }: { id: number; canReview: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [remarks, setRemarks] = useState('');
  const [error, setError] = useState<string | null>(null);
  const { data: app } = useQuery({ queryKey: ['technician-application', id], queryFn: () => technicianOnboardingApi.get(id) });

  const reviewMut = useMutation({
    mutationFn: (decision: TechnicianReviewDecision) => technicianOnboardingApi.review(id, decision, remarks || undefined),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['technician-applications'] }); qc.invalidateQueries({ queryKey: ['technician-application', id] }); setRemarks(''); setError(null); },
    onError: (e) => setError(extractApiError(e)),
  });
  const decisions = app && STAGE_DECISIONS[app.current_stage] ? STAGE_DECISIONS[app.current_stage] : [];
  const terminal = app ? ['approved', 'active', 'rejected'].includes(app.status) : false;

  return (
    <Dialog open fullWidth maxWidth="md" onClose={onClose}>
      <DialogTitle>{app ? `${app.application_code} — ${app.technician_name}` : 'Loading…'}</DialogTitle>
      <DialogContent dividers>
        {app && (
          <Stack spacing={2}>
            {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
            <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
              <Field label="Type" value={human(app.technician_type)} />
              <Field label="Mobile" value={app.mobile} />
              <Field label="Aadhaar" value={app.aadhaar} />
              <Field label="Skills" value={(app.skill_categories ?? []).map(human).join(', ') || null} />
              <Field label="Experience" value={app.experience_years != null ? `${app.experience_years}y (${human(app.experience_level)})` : null} />
              <Box><Typography variant="caption" color="text.secondary">Status</Typography><Box><Chip size="small" color={statusColor(app.status)} label={human(app.status)} /></Box></Box>
            </Stack>

            <Divider />
            <Typography variant="subtitle2">Approval workflow</Typography>
            <Stack spacing={0.5}>
              {(app.workflow ?? []).map((w) => (
                <Stack key={w.id} direction="row" spacing={1} alignItems="center">
                  <Chip size="small" label={`${w.stage_order}. ${human(w.stage)}`} variant="outlined" />
                  <Chip size="small" color={statusColor(w.status)} label={human(w.status)} />
                  {w.remarks && <Typography variant="caption" color="text.secondary">— {w.remarks}</Typography>}
                </Stack>
              ))}
            </Stack>

            {canReview && !terminal && decisions.length > 0 && (
              <>
                <Divider />
                <Typography variant="subtitle2">Action — {human(app.current_stage)} stage</Typography>
                <TextField size="small" label="Remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} multiline minRows={2} />
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {decisions.map((d) => <Button key={d} variant="contained" color={DECISION_COLOR[d]} disabled={reviewMut.isPending} onClick={() => reviewMut.mutate(d)}>{DECISION_LABEL[d]}</Button>)}
                </Stack>
              </>
            )}
            {terminal && <Alert severity={app.status === 'rejected' ? 'error' : 'success'}>This application is {human(app.status)}{app.technician_id ? ` — technician master #${app.technician_id} created${app.login_user_id ? ' with portal login' : ''}.` : '.'}</Alert>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}

function StatusDialog({ app, onClose, onDone }: { app: TechnicianApplication; onClose: () => void; onDone: () => void }) {
  const [statusVal, setStatusVal] = useState(app.operational_status || 'active');
  const [error, setError] = useState<string | null>(null);
  const mut = useMutation({ mutationFn: () => technicianOnboardingApi.changeStatus(app.id, statusVal), onSuccess: onDone, onError: (e) => setError(extractApiError(e)) });
  return (
    <Dialog open fullWidth maxWidth="xs" onClose={onClose}>
      <DialogTitle>Change Technician Status — {app.application_code}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} mt={1}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField select label="Status" value={statusVal} onChange={(e) => setStatusVal(e.target.value)}>
            {OPERATIONAL_STATUSES.map((s) => <MenuItem key={s} value={s}>{human(s)}</MenuItem>)}
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={mut.isPending} onClick={() => mut.mutate()}>Update</Button>
      </DialogActions>
    </Dialog>
  );
}

function Field({ label, value }: { label: string; value?: string | null }) {
  return <Box><Typography variant="caption" color="text.secondary">{label}</Typography><Typography>{value ?? '—'}</Typography></Box>;
}
